import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { supabase, Imovel } from '../../lib/supabase';
import { useSEO } from '../../hooks/useSEO';
import { generatePropertyListSchema, generateBreadcrumbSchema } from '../../utils/seo';
import { trackSearch } from '../../utils/analytics';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import Breadcrumb from '../../components/base/Breadcrumb';

export default function ImoveisPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [imoveis, setImoveis] = useState<Imovel[]>([]);
  const [loading, setLoading] = useState(true);
  const [favoritos, setFavoritos] = useState<string[]>([]);
  const [comparacao, setComparacao] = useState<string[]>([]);
  
  // Filtros
  const [tipo, setTipo] = useState(searchParams.get('tipo') || '');
  const [cidade, setCidade] = useState(searchParams.get('cidade') || '');
  const [bairro, setBairro] = useState(searchParams.get('bairro') || '');
  const [precoMin, setPrecoMin] = useState(searchParams.get('precoMin') || '');
  const [precoMax, setPrecoMax] = useState(searchParams.get('precoMax') || '');
  const [quartos, setQuartos] = useState(searchParams.get('quartos') || '');

  useEffect(() => {
    loadImoveis();
    loadFavoritos();
    loadComparacao();
  }, [tipo, cidade, bairro, precoMin, precoMax, quartos]);

  const loadFavoritos = () => {
    const saved = localStorage.getItem('favoritos');
    if (saved) {
      setFavoritos(JSON.parse(saved));
    }
  };

  const loadComparacao = () => {
    const saved = localStorage.getItem('comparacao');
    if (saved) {
      setComparacao(JSON.parse(saved));
    }
  };

  const toggleFavorito = (imovelId: string) => {
    let newFavoritos: string[];
    if (favoritos.includes(imovelId)) {
      newFavoritos = favoritos.filter(id => id !== imovelId);
    } else {
      newFavoritos = [...favoritos, imovelId];
    }
    setFavoritos(newFavoritos);
    localStorage.setItem('favoritos', JSON.stringify(newFavoritos));
  };

  const toggleComparacao = (imovelId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    let newComparacao: string[];
    if (comparacao.includes(imovelId)) {
      newComparacao = comparacao.filter(id => id !== imovelId);
    } else {
      if (comparacao.length >= 3) {
        alert('Você pode comparar no máximo 3 imóveis por vez');
        return;
      }
      newComparacao = [...comparacao, imovelId];
    }
    setComparacao(newComparacao);
    localStorage.setItem('comparacao', JSON.stringify(newComparacao));
  };

  const irParaComparacao = () => {
    if (comparacao.length === 0) {
      alert('Selecione pelo menos 1 imóvel para comparar');
      return;
    }
    navigate(`/comparar-imoveis?ids=${comparacao.join(',')}`);
  };

  const loadImoveis = async () => {
    try {
      setLoading(true);
      
      let query = supabase
        .from('imoveis')
        .select(`
          *,
          fotos:imovel_fotos(url)
        `)
        .eq('status', 'disponivel');

      if (tipo) query = query.eq('tipo', tipo);
      if (cidade) query = query.ilike('cidade', `%${cidade}%`);
      if (bairro) query = query.ilike('bairro', `%${bairro}%`);
      if (precoMin) query = query.gte('preco', parseFloat(precoMin));
      if (precoMax) query = query.lte('preco', parseFloat(precoMax));
      if (quartos) query = query.gte('quartos', parseInt(quartos));

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;
      setImoveis(data || []);
      
      // Track search
      if (tipo || cidade || bairro) {
        trackSearch(`${tipo} ${cidade} ${bairro}`.trim());
      }
    } catch (error) {
      console.error('Erro ao carregar imóveis:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = () => {
    const params: any = {};
    if (tipo) params.tipo = tipo;
    if (cidade) params.cidade = cidade;
    if (bairro) params.bairro = bairro;
    if (precoMin) params.precoMin = precoMin;
    if (precoMax) params.precoMax = precoMax;
    if (quartos) params.quartos = quartos;
    setSearchParams(params);
  };

  // SEO
  useSEO({
    title: 'Imóveis em Aracaju e Barra dos Coqueiros - Sergipe | Mateus Oliveira',
    description: 'Encontre casas, apartamentos e terrenos de médio e alto padrão em Aracaju e Barra dos Coqueiros. Compra, venda e aluguel com atendimento especializado. Avaliação gratuita.',
    keywords: 'imóveis aracaju, imóveis barra dos coqueiros, casas aracaju, apartamentos aracaju, terrenos sergipe, comprar imóvel aracaju, vender imóvel sergipe',
    canonical: window.location.origin + '/imoveis',
    schema: imoveis.length > 0 ? [
      generatePropertyListSchema(imoveis),
      generateBreadcrumbSchema([{ name: 'Imóveis', url: '/imoveis' }]),
    ] : generateBreadcrumbSchema([{ name: 'Imóveis', url: '/imoveis' }]),
  });

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Breadcrumb items={[{ name: 'Imóveis', url: '/imoveis' }]} />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
                  Todos os <span className="text-orange-500">Imóveis</span>
                </h1>
                <p className="text-xl text-gray-600">
                  Encontre o imóvel perfeito em Aracaju e Barra dos Coqueiros
                </p>
              </div>
              {comparacao.length > 0 && (
                <button
                  onClick={irParaComparacao}
                  className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 px-6 rounded-lg transition-all whitespace-nowrap flex items-center gap-2 cursor-pointer"
                >
                  <i className="ri-scales-line text-xl"></i>
                  Comparar ({comparacao.length})
                </button>
              )}
            </div>
          </motion.div>

          {/* Filtros */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-lg p-6 mb-8"
          >
            <h2 className="text-xl font-bold text-gray-800 mb-4">Filtrar Imóveis</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <select
                value={tipo}
                onChange={(e) => setTipo(e.target.value)}
                className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <option value="">Tipo</option>
                <option value="Casa">Casa</option>
                <option value="Apartamento">Apartamento</option>
                <option value="Terreno">Terreno</option>
                <option value="Cobertura">Cobertura</option>
              </select>

              <input
                type="text"
                value={cidade}
                onChange={(e) => setCidade(e.target.value)}
                placeholder="Cidade"
                className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />

              <input
                type="text"
                value={bairro}
                onChange={(e) => setBairro(e.target.value)}
                placeholder="Bairro"
                className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />

              <input
                type="number"
                value={precoMin}
                onChange={(e) => setPrecoMin(e.target.value)}
                placeholder="Preço mín."
                className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />

              <input
                type="number"
                value={precoMax}
                onChange={(e) => setPrecoMax(e.target.value)}
                placeholder="Preço máx."
                className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />

              <select
                value={quartos}
                onChange={(e) => setQuartos(e.target.value)}
                className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <option value="">Quartos</option>
                <option value="1">1+</option>
                <option value="2">2+</option>
                <option value="3">3+</option>
                <option value="4">4+</option>
              </select>
            </div>
            <div className="mt-4 flex gap-4">
              <button
                onClick={handleFilterChange}
                className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 px-6 rounded-lg transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-search-line mr-2"></i>
                Buscar
              </button>
              <button
                onClick={() => {
                  setTipo('');
                  setCidade('');
                  setBairro('');
                  setPrecoMin('');
                  setPrecoMax('');
                  setQuartos('');
                  setSearchParams({});
                }}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all font-bold whitespace-nowrap cursor-pointer"
              >
                Limpar
              </button>
            </div>
          </motion.div>

          {/* Lista de Imóveis */}
          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
            </div>
          ) : imoveis.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6" data-product-shop>
              {imoveis.map((imovel, index) => (
                <motion.div
                  key={imovel.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                >
                  <div className="relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all hover:-translate-y-2 group" data-product-shop>
                    <Link to={`/imovel/${imovel.slug}`} className="block">
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={imovel.fotos?.[0]?.url || 'https://via.placeholder.com/400x300'}
                          alt={imovel.titulo}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                        {imovel.destaque && (
                          <div className="absolute top-3 right-3 bg-orange-500 text-white px-2 py-1 rounded-full text-xs font-bold">
                            Destaque
                          </div>
                        )}
                      </div>
                      <div className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-semibold text-orange-500 uppercase">
                            {imovel.tipo}
                          </span>
                          <span className="text-xs text-gray-500">{imovel.codigo}</span>
                        </div>
                        <h3 className="text-base font-bold text-gray-800 mb-2 group-hover:text-orange-500 transition-colors line-clamp-2">
                          {imovel.titulo}
                        </h3>
                        <p className="text-gray-600 text-xs mb-3 flex items-center">
                          <i className="ri-map-pin-line mr-1"></i>
                          {imovel.bairro}, {imovel.cidade}
                        </p>
                        <div className="flex items-center justify-between text-xs text-gray-600 mb-3 pb-3 border-b">
                          <span className="flex items-center">
                            <i className="ri-hotel-bed-line mr-1"></i>
                            {imovel.quartos}
                          </span>
                          <span className="flex items-center">
                            <i className="ri-shower-line mr-1"></i>
                            {imovel.banheiros}
                          </span>
                          <span className="flex items-center">
                            <i className="ri-car-line mr-1"></i>
                            {imovel.vagas}
                          </span>
                          <span className="flex items-center">
                            <i className="ri-ruler-line mr-1"></i>
                            {imovel.area}m²
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-lg font-bold text-orange-500">
                              {new Intl.NumberFormat('pt-BR', {
                                style: 'currency',
                                currency: 'BRL',
                              }).format(imovel.preco)}
                            </p>
                          </div>
                          <button className="bg-orange-500 text-white px-3 py-1.5 rounded-lg hover:bg-orange-600 transition-colors text-xs whitespace-nowrap cursor-pointer">
                            Ver Detalhes
                          </button>
                        </div>
                      </div>
                    </Link>
                    {/* Botão de Favorito */}
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        toggleFavorito(imovel.id);
                      }}
                      className="absolute top-3 left-3 w-9 h-9 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-all shadow-lg z-10 cursor-pointer"
                      title={favoritos.includes(imovel.id) ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
                    >
                      <i className={`${favoritos.includes(imovel.id) ? 'ri-heart-fill text-red-500' : 'ri-heart-line text-gray-700'} text-xl`}></i>
                    </button>
                    {/* Botão de Comparar */}
                    <button
                      onClick={(e) => toggleComparacao(imovel.id, e)}
                      className={`absolute bottom-3 right-3 w-9 h-9 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-all shadow-lg z-10 cursor-pointer ${
                        comparacao.includes(imovel.id) 
                          ? 'bg-orange-500 text-white' 
                          : 'bg-white/90 text-gray-700'
                      }`}
                      title={comparacao.includes(imovel.id) ? 'Remover da comparação' : 'Adicionar à comparação'}
                    >
                      <i className="ri-scales-line text-xl"></i>
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <i className="ri-home-line text-6xl text-gray-300 mb-4"></i>
              <p className="text-xl text-gray-600">Nenhum imóvel encontrado com os filtros selecionados</p>
            </div>
          )}

          {/* SEO Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-16 bg-white rounded-2xl p-8 shadow-lg"
          >
            <h2 className="text-3xl font-bold text-gray-800 mb-6">
              Imóveis de Qualidade em Sergipe
            </h2>
            <div className="prose prose-lg max-w-none text-gray-600">
              <p>
                A <strong>Mateus Oliveira Imóveis</strong> é especializada em imóveis de médio e alto padrão 
                em <strong>Aracaju</strong> e <strong>Barra dos Coqueiros</strong>. Oferecemos um portfólio 
                completo de casas, apartamentos e terrenos nas melhores localizações de Sergipe.
              </p>
              <h3 className="text-2xl font-bold text-gray-800 mt-6 mb-4">
                Por que escolher a Mateus Oliveira?
              </h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Atendimento personalizado e consultivo</li>
                <li>Avaliação gratuita do seu imóvel</li>
                <li>Acompanhamento completo do processo</li>
                <li>Expertise no mercado local de Sergipe</li>
                <li>Suporte com financiamento e documentação</li>
              </ul>
              <p className="mt-6">
                Entre em contato pelo WhatsApp <strong>(79) 99988-1002</strong> e encontre o imóvel ideal 
                para você e sua família.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
      <WhatsAppButton />
      <Footer />
    </>
  );
}
